import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, Divider, IconButton, Tooltip,
  InputAdornment, Switch, Snackbar, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Payment, Refresh, Save, CheckCircle, Error as ErrorIcon,
  Visibility, VisibilityOff, Lock, Security, PowerSettingsNew,
  Speed, Receipt, Warning, ContentCopy,
  OpenInNew, Shield, Timeline, SyncProblem, CurrencyRupee,
  TrendingUp, Webhook,
} from '@mui/icons-material';
import { paymentGatewayAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtCurrency = (v) => {
  if (v == null) return '—';
  return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, sub }) => (
  <Card sx={{
    borderRadius: 2, border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20` },
  }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8', mt: 0.2 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════════

const Section = ({ icon, title, color, children, action }) => (
  <Box>
    <Box className="flex items-center justify-between mb-1.5">
      <Box className="flex items-center gap-1.5">
        {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', minWidth: 80 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const PaymentGatewayPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testTimestamp, setTestTimestamp] = useState(null);
  const [txSummary, setTxSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);

  // API key form
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showKeyId, setShowKeyId] = useState(false);
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Audit log (session)
  const [auditLog, setAuditLog] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Audit helper ──
  const addAudit = useCallback((action, detail) => {
    setAuditLog(prev => [
      { timestamp: new Date().toISOString(), action, detail },
      ...prev.slice(0, 29),
    ]);
  }, []);

  // ── Load Data ──
  const loadStatus = useCallback(async () => {
    try {
      const res = await paymentGatewayAPI.getStatus();
      setGatewayStatus(res.data);
    } catch (err) {
      console.error('Error loading payment gateway status:', err);
      setSnackbar({ open: true, message: 'Failed to load payment gateway status', severity: 'error' });
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const [txRes, summaryRes] = await Promise.all([
        paymentGatewayAPI.getTransactions({ limit: 10, offset: 0, status: 'failed' }),
        paymentGatewayAPI.getTransactionSummary(),
      ]);
      // Failed transactions for the table
      setTransactions(txRes.data.transactions || []);
      setTxTotal(txRes.data.total || 0);
      setTxSummary(summaryRes.data);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    await Promise.all([loadStatus(), loadTransactions()]);
    setLoading(false);
    setRefreshing(false);
  }, [loadStatus, loadTransactions]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Handlers ──
  const handleSaveKey = async (settingKey, value, label) => {
    if (!value.trim()) {
      setSnackbar({ open: true, message: `Please enter a ${label}`, severity: 'error' });
      return;
    }
    try {
      setSaving(prev => ({ ...prev, [settingKey]: true }));
      await paymentGatewayAPI.updateSetting(settingKey, value.trim());
      setSnackbar({ open: true, message: `${label} saved successfully!`, severity: 'success' });
      addAudit(`${label} Updated`, `${label} was updated (encrypted)`);
      if (settingKey === 'razorpay_key_id') setKeyId('');
      if (settingKey === 'razorpay_key_secret') setKeySecret('');
      if (settingKey === 'razorpay_webhook_secret') setWebhookSecret('');
      await loadStatus();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || `Failed to save ${label}`, severity: 'error' });
    } finally {
      setSaving(prev => ({ ...prev, [settingKey]: false }));
    }
  };

  const handleToggle = async () => {
    try {
      setToggling(true);
      const newState = !gatewayStatus?.enabled;
      await paymentGatewayAPI.toggle(newState);
      setSnackbar({ open: true, message: `Payment gateway ${newState ? 'enabled' : 'disabled'} successfully`, severity: 'success' });
      addAudit('Gateway Toggled', `Payment gateway ${newState ? 'enabled' : 'disabled'}`);
      await loadStatus();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to toggle payment gateway', severity: 'error' });
    } finally {
      setToggling(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const res = await paymentGatewayAPI.testConnection();
      setTestResult(res.data);
      setTestTimestamp(new Date().toISOString());
      addAudit('Connection Test', res.data.success ? `Passed (${res.data.mode || 'test'} mode)` : `Failed: ${res.data.message}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, message: msg });
      setTestTimestamp(new Date().toISOString());
      addAudit('Connection Test', `Failed: ${msg}`);
    } finally {
      setTesting(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    const url = `${window.location.protocol}//${window.location.hostname}:8000/api/v1/payments/webhook`;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Webhook URL copied to clipboard!', severity: 'success' });
  };

  // ── Derived ──
  const isActive = gatewayStatus?.can_accept_payments || false;
  const isEnabled = gatewayStatus?.enabled || false;
  const keysConfigured = gatewayStatus?.key_id_configured && gatewayStatus?.key_secret_configured;
  const mode = gatewayStatus?.mode || 'test';

  // Failed transactions (last 10)
  const failedTx = useMemo(() => transactions.slice(0, 10), [transactions]);

  // ── Loading ──
  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading financial console…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ HEADER ══════════ */}
      <Box className="flex items-center justify-between mb-3">
        <Box className="flex items-center gap-2">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Payment sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Financial Control Console
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              Razorpay Payment Gateway — Configuration & Monitoring
            </Typography>
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {/* 6️⃣ Enable/Disable toggle */}
          <Box className="flex items-center gap-1.5" sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: isEnabled ? '#dcfce7' : '#fef2f2', border: `1px solid ${isEnabled ? '#22c55e' : '#fca5a5'}` }}>
            <PowerSettingsNew sx={{ fontSize: 16, color: isEnabled ? '#16a34a' : '#dc2626' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isEnabled ? '#16a34a' : '#dc2626' }}>
              {toggling ? 'Toggling…' : isEnabled ? 'Enabled' : 'Disabled'}
            </Typography>
            <Switch
              size="small" checked={isEnabled}
              onChange={handleToggle}
              disabled={toggling || !keysConfigured}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' },
              }}
            />
          </Box>
          {mode && (
            <Chip label={mode === 'live' ? 'LIVE' : 'TEST'} size="small"
              sx={{ height: 22, fontSize: '0.62rem', fontWeight: 800, bgcolor: mode === 'live' ? '#fef2f2' : '#dbeafe', color: mode === 'live' ? '#dc2626' : '#2563eb', border: `1px solid ${mode === 'live' ? '#fca5a5' : '#93c5fd'}` }} />
          )}
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadAll(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* ══════════ 2. GATEWAY STATUS PANEL ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden', border: `2px solid ${isActive ? '#22c55e' : '#fca5a5'}` }}>
        <Box sx={{ px: 2, py: 1.2, bgcolor: isActive ? '#f0fdf4' : '#fef2f2' }}>
          <Box className="flex items-center justify-between">
            <Box className="flex items-center gap-2">
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: isActive ? '#dcfce7' : '#fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isActive ? <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} /> : <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: isActive ? '#16a34a' : '#dc2626' }}>
                  {isActive ? 'Gateway Active' : 'Gateway Inactive'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                  {gatewayStatus?.status_message || 'Loading…'}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-1.5">
              <Chip label={isActive ? 'Active' : 'Inactive'} size="small"
                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: isActive ? '#dcfce7' : '#fef2f2', color: isActive ? '#16a34a' : '#dc2626' }} />
              <Chip label={`Mode: ${mode.toUpperCase()}`} size="small"
                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: mode === 'live' ? '#fef2f2' : '#dbeafe', color: mode === 'live' ? '#dc2626' : '#2563eb' }} />
              {testTimestamp && (
                <Chip label={`Last test: ${fmtTime(testTimestamp)}`} size="small"
                  sx={{ height: 20, fontSize: '0.55rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
              )}
            </Box>
          </Box>
        </Box>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
            <InfoRow label="Key ID" value={
              gatewayStatus?.key_id_configured
                ? <Box className="flex items-center gap-1"><Lock sx={{ fontSize: 11, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.66rem', fontWeight: 600, fontFamily: 'monospace' }}>{gatewayStatus.key_id_masked}</Typography></Box>
                : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
            } />
            <InfoRow label="Key Secret" value={
              gatewayStatus?.key_secret_configured
                ? <Box className="flex items-center gap-1"><Lock sx={{ fontSize: 11, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.66rem', fontWeight: 600, fontFamily: 'monospace' }}>{gatewayStatus.key_secret_masked}</Typography></Box>
                : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
            } />
            <InfoRow label="Webhook Secret" value={
              gatewayStatus?.webhook_secret_configured
                ? <Box className="flex items-center gap-1"><Lock sx={{ fontSize: 11, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.66rem', fontWeight: 600, fontFamily: 'monospace' }}>{gatewayStatus.webhook_secret_masked}</Typography></Box>
                : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#d97706' }} />
            } />
            <InfoRow label="Last Webhook" value={testTimestamp ? fmtDateTime(testTimestamp) : 'No events yet'} />
          </Box>
        </CardContent>
      </Card>

      {/* ══════════ 3. TRANSACTION KPI STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-3">
        <KPICard icon={<Receipt />} label="Total Transactions" color="#2563eb"
          value={txSummary?.total_orders ?? '—'} />
        <KPICard icon={<CheckCircle />} label="Success" color="#16a34a"
          value={txSummary?.paid_orders ?? '—'} />
        <KPICard icon={<ErrorIcon />} label="Failed" color="#dc2626"
          value={txSummary?.failed_orders ?? '—'} />
        <KPICard icon={<TrendingUp />} label="Pending" color="#d97706"
          value={txSummary?.pending_orders ?? '—'} />
        <KPICard icon={<CurrencyRupee />} label="Total Collected" color="#7c3aed"
          value={txSummary ? fmtCurrency(txSummary.total_amount_collected) : '—'} />
      </Box>

      {/* ══════════ TWO-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ minHeight: 420 }}>

        {/* ── LEFT COLUMN ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 4️⃣ Failed Transaction Table (last 10) */}
          <Section icon={<SyncProblem />} title="Failed Transactions (Last 10)" color="#dc2626"
            action={
              <Button size="small" variant="outlined" onClick={() => loadAll(true)} disabled={refreshing}
                startIcon={<Refresh />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.62rem' }}>
                Refresh
              </Button>
            }>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              {failedTx.length === 0 ? (
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#22c55e', opacity: 0.4, mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8' }}>
                    No failed transactions
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', mt: 0.3 }}>
                    All payment processing is running smoothly.
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Timestamp</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Order ID</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }} align="right">Amount</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>User / Policy</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedTx.map((tx, i) => (
                        <TableRow key={tx.id} sx={{ bgcolor: i === 0 ? '#fef2f2' : 'transparent' }}>
                          <TableCell sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#64748b', py: 0.8, whiteSpace: 'nowrap' }}>
                            {fmtDateTime(tx.created_at)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#1e293b', py: 0.8, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tx.order_id}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', py: 0.8, whiteSpace: 'nowrap' }}>
                            {fmtCurrency(tx.amount)}
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={tx.status === 'paid' ? 'Paid' : tx.status === 'created' ? 'Pending' : 'Failed'} size="small"
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 700,
                                bgcolor: tx.status === 'paid' ? '#dcfce7' : tx.status === 'created' ? '#fef3c7' : '#fef2f2',
                                color: tx.status === 'paid' ? '#16a34a' : tx.status === 'created' ? '#d97706' : '#dc2626',
                              }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>
                            User #{tx.user_id}{tx.policy_id ? ` / Policy #${tx.policy_id}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {txTotal > 10 && (
                <Box sx={{ px: 2, py: 0.8, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center' }}>
                    Showing 10 of {txTotal} failed transactions
                  </Typography>
                </Box>
              )}
            </Card>
          </Section>

          {/* 5️⃣ Webhook Status */}
          <Section icon={<Webhook />} title="Webhook Status" color="#0891b2">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box className="grid grid-cols-1 gap-2">
                  {/* Webhook URL */}
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', mb: 0.5 }}>Webhook URL</Typography>
                    <Box className="flex items-center gap-1" sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#1e293b', flex: 1, wordBreak: 'break-all' }}>
                        {`${window.location.protocol}//${window.location.hostname}:8000/api/v1/payments/webhook`}
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton size="small" onClick={handleCopyWebhookUrl}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box className="grid grid-cols-3 gap-2">
                    {/* Last Webhook Event */}
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', mb: 0.3 }}>Last Event</Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>
                        {testTimestamp ? fmtDateTime(testTimestamp) : 'No events'}
                      </Typography>
                    </Box>

                    {/* Signature Verified */}
                    <Box sx={{ p: 1.5, bgcolor: gatewayStatus?.webhook_secret_configured ? '#f0fdf4' : '#fef3c7', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', mb: 0.3 }}>Signature Verification</Typography>
                      <Box className="flex items-center gap-1">
                        {gatewayStatus?.webhook_secret_configured
                          ? <><CheckCircle sx={{ fontSize: 14, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#16a34a' }}>Active</Typography></>
                          : <><Warning sx={{ fontSize: 14, color: '#d97706' }} /><Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#d97706' }}>Not Configured</Typography></>
                        }
                      </Box>
                    </Box>

                    {/* Events to enable */}
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', mb: 0.3 }}>Required Events</Typography>
                      <Box className="flex flex-wrap gap-0.5">
                        <Chip label="payment.captured" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 600, bgcolor: '#dbeafe', color: '#2563eb' }} />
                        <Chip label="payment.failed" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 600, bgcolor: '#fef2f2', color: '#dc2626' }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Section>

          {/* Test Connection inline */}
          <Section icon={<Speed />} title="API Connection Test" color="#7c3aed"
            action={
              <Button size="small" variant={testResult?.success ? 'outlined' : 'contained'}
                onClick={handleTestConnection}
                disabled={testing || !keysConfigured}
                startIcon={testing ? null : <Refresh />}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: '0.68rem',
                  ...(testResult?.success
                    ? { borderColor: '#16a34a', color: '#16a34a' }
                    : { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }),
                }}>
                {testing ? 'Testing…' : 'Test Now'}
              </Button>
            }>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {!keysConfigured && (
                  <Alert severity="warning" sx={{ borderRadius: 2, mb: 1, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    Configure API keys first to test connection.
                  </Alert>
                )}
                {testResult ? (
                  <Box>
                    <Box className="flex items-center gap-2 mb-1">
                      {testResult.success
                        ? <CheckCircle sx={{ fontSize: 20, color: '#16a34a' }} />
                        : <ErrorIcon sx={{ fontSize: 20, color: '#dc2626' }} />}
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: testResult.success ? '#16a34a' : '#dc2626' }}>
                        {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                      </Typography>
                      {testResult.mode && (
                        <Chip label={`${testResult.mode.toUpperCase()} Mode`} size="small"
                          sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: testResult.mode === 'live' ? '#fef2f2' : '#dbeafe', color: testResult.mode === 'live' ? '#dc2626' : '#2563eb' }} />
                      )}
                      {testTimestamp && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', ml: 'auto' }}>{fmtDateTime(testTimestamp)}</Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: testResult.success ? '#16a34a' : '#dc2626' }}>
                      {testResult.message}
                    </Typography>
                  </Box>
                ) : (
                  <Box className="text-center" sx={{ py: 1.5 }}>
                    <Speed sx={{ fontSize: 28, color: '#cbd5e1', mb: 0.5 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      Click "Test Now" to verify Razorpay API connectivity
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Section>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* API Configuration */}
          <Section icon={<Lock />} title="API Configuration" color="#0f172a">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Alert severity="info" icon={<Security sx={{ fontSize: 16 }} />} sx={{ borderRadius: 2, mb: 2, '& .MuiAlert-message': { fontSize: '0.65rem' } }}>
                  API keys are stored encrypted. Never exposed to frontend.
                </Alert>

                {/* Key ID */}
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center justify-between mb-0.5">
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>Razorpay Key ID</Typography>
                    {gatewayStatus?.key_id_configured
                      ? <Chip label="Configured" size="small" icon={<CheckCircle sx={{ fontSize: 10 }} />} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-icon': { color: '#16a34a' } }} />
                      : <Chip label="Missing" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
                    }
                  </Box>
                  {gatewayStatus?.key_id_configured && (
                    <Typography sx={{ fontSize: '0.58rem', fontFamily: 'monospace', color: '#94a3b8', mb: 0.5 }}>{gatewayStatus.key_id_masked}</Typography>
                  )}
                  <TextField
                    fullWidth size="small"
                    type={showKeyId ? 'text' : 'password'}
                    placeholder="rzp_test_XXX or rzp_live_XXX"
                    value={keyId} onChange={(e) => setKeyId(e.target.value)}
                    sx={{ mb: 0.8, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowKeyId(!showKeyId)} edge="end" size="small">{showKeyId ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton></InputAdornment> }}
                  />
                  <Button fullWidth size="small" variant="contained"
                    startIcon={saving.razorpay_key_id ? null : <Save />}
                    onClick={() => handleSaveKey('razorpay_key_id', keyId, 'Key ID')}
                    disabled={saving.razorpay_key_id || !keyId.trim()}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.6, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontSize: '0.72rem' }}>
                    {saving.razorpay_key_id ? 'Saving…' : 'Save Key ID'}
                  </Button>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Key Secret */}
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center justify-between mb-0.5">
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>Key Secret</Typography>
                    {gatewayStatus?.key_secret_configured
                      ? <Chip label="Configured" size="small" icon={<CheckCircle sx={{ fontSize: 10 }} />} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-icon': { color: '#16a34a' } }} />
                      : <Chip label="Missing" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
                    }
                  </Box>
                  {gatewayStatus?.key_secret_configured && (
                    <Typography sx={{ fontSize: '0.58rem', fontFamily: 'monospace', color: '#94a3b8', mb: 0.5 }}>{gatewayStatus.key_secret_masked}</Typography>
                  )}
                  <TextField
                    fullWidth size="small"
                    type={showKeySecret ? 'text' : 'password'}
                    placeholder="Enter Razorpay Key Secret"
                    value={keySecret} onChange={(e) => setKeySecret(e.target.value)}
                    sx={{ mb: 0.8, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowKeySecret(!showKeySecret)} edge="end" size="small">{showKeySecret ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton></InputAdornment> }}
                  />
                  <Button fullWidth size="small" variant="contained"
                    startIcon={saving.razorpay_key_secret ? null : <Save />}
                    onClick={() => handleSaveKey('razorpay_key_secret', keySecret, 'Key Secret')}
                    disabled={saving.razorpay_key_secret || !keySecret.trim()}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.6, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontSize: '0.72rem' }}>
                    {saving.razorpay_key_secret ? 'Saving…' : 'Save Key Secret'}
                  </Button>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Webhook Secret */}
                <Box>
                  <Box className="flex items-center justify-between mb-0.5">
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>Webhook Secret</Typography>
                    {gatewayStatus?.webhook_secret_configured
                      ? <Chip label="Configured" size="small" icon={<CheckCircle sx={{ fontSize: 10 }} />} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-icon': { color: '#16a34a' } }} />
                      : <Chip label="Recommended" size="small" icon={<Warning sx={{ fontSize: 10 }} />} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#d97706', '& .MuiChip-icon': { color: '#d97706' } }} />
                    }
                  </Box>
                  {gatewayStatus?.webhook_secret_configured && (
                    <Typography sx={{ fontSize: '0.58rem', fontFamily: 'monospace', color: '#94a3b8', mb: 0.5 }}>{gatewayStatus.webhook_secret_masked}</Typography>
                  )}
                  <TextField
                    fullWidth size="small"
                    type={showWebhookSecret ? 'text' : 'password'}
                    placeholder="Enter Razorpay Webhook Secret"
                    value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
                    sx={{ mb: 0.8, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowWebhookSecret(!showWebhookSecret)} edge="end" size="small">{showWebhookSecret ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton></InputAdornment> }}
                  />
                  <Button fullWidth size="small" variant="contained"
                    startIcon={saving.razorpay_webhook_secret ? null : <Save />}
                    onClick={() => handleSaveKey('razorpay_webhook_secret', webhookSecret, 'Webhook Secret')}
                    disabled={saving.razorpay_webhook_secret || !webhookSecret.trim()}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.6, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontSize: '0.72rem' }}>
                    {saving.razorpay_webhook_secret ? 'Saving…' : 'Save Webhook Secret'}
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mt: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.62rem' } }}>
                  Get keys from{' '}
                  <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 700 }}>
                    Razorpay Dashboard <OpenInNew sx={{ fontSize: 10, verticalAlign: 'middle' }} />
                  </a>
                </Alert>
              </CardContent>
            </Card>
          </Section>
        </Box>
      </Box>

      {/* ══════════ 7. AUDIT LOG ══════════ */}
      <Box sx={{ mt: 2.5 }}>
        <Section icon={<Timeline />} title="Audit Log" color="#64748b"
          action={auditLog.length > 0 && (
            <Button size="small" onClick={() => setAuditLog([])} sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>Clear</Button>
          )}>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Static audit entries from API data */}
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>Key ID Last Updated</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {gatewayStatus?.key_id_updated_at ? fmtDateTime(gatewayStatus.key_id_updated_at) : 'Never configured'}
                  </Typography>
                </Box>
              </Box>
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>Key Secret Last Updated</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {gatewayStatus?.key_secret_updated_at ? fmtDateTime(gatewayStatus.key_secret_updated_at) : 'Never configured'}
                  </Typography>
                </Box>
              </Box>
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0891b2', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>Webhook Secret Last Updated</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {gatewayStatus?.webhook_secret_updated_at ? fmtDateTime(gatewayStatus.webhook_secret_updated_at) : 'Never configured'}
                  </Typography>
                </Box>
              </Box>
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isEnabled ? '#16a34a' : '#dc2626', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                    Gateway {isEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Current state</Typography>
                </Box>
              </Box>

              {/* Session audit entries */}
              {auditLog.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1 }}>
                    Session Activity
                  </Typography>
                  {auditLog.map((entry, i) => (
                    <Box key={i} className="flex items-start gap-2 mb-1.5">
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#64748b', mt: 0.6, flexShrink: 0 }} />
                      <Box className="min-w-0 flex-1">
                        <Box className="flex items-center gap-1">
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e293b' }}>{entry.action}</Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', ml: 'auto' }}>{fmtTime(entry.timestamp)}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{entry.detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              <Divider sx={{ my: 1 }} />
              <Box className="flex items-center gap-1">
                <Shield sx={{ fontSize: 12, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8' }}>
                  All payment gateway changes are audit-logged. Keys are stored encrypted via HMAC-SHA256.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Section>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentGatewayPage;
